import { useState, useEffect, useCallback } from 'react';
import type { TaskDropdownCatalog } from '@techgitter/contracts';
import { getTaskDropdowns } from '../services/taskDropdownService';

const EMPTY_CATALOG: TaskDropdownCatalog = { schemaVersion: 1, dropdowns: [] };

/**
 * Load the reusable task-dropdown option sets. Used both by the checklist
 * (to resolve dropdown-task options) and the admin editor.
 */
export function useTaskDropdowns() {
  const [dropdownCatalog, setDropdownCatalog] =
    useState<TaskDropdownCatalog>(EMPTY_CATALOG);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (signal?: AbortSignal) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await getTaskDropdowns(signal);
      if (signal?.aborted) return;
      setDropdownCatalog(result);
    } catch (err: any) {
      if (err.name === 'AbortError' || signal?.aborted) return;
      setError(err.message || 'Failed to load task dropdowns');
    } finally {
      if (!signal?.aborted) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const refresh = useCallback(async () => {
    await load();
  }, [load]);

  return { dropdownCatalog, isLoading, error, refresh };
}
